import { isArray, isBoolean, isNumber, isString, maxBy, remove, startCase } from "lodash";
import { useEffect, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import { Button, Checkbox, DropdownProps, Form, Modal, TextAreaProps } from "semantic-ui-react";
// @ts-ignore
import "./SetConfigurationValueModal.css";
import { removeTypenameDeep } from "../utils/apollo";
import { isMultipleChoiceOption } from "../models/AppConfiguration";

const INT32_MAX_VALUE = Math.pow(2, 31) - 1;

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (value: object) => void;
  submitInProgress: boolean;
  title?: string;
  initialValue?: object | null;
  configKey?: string;
  nullableValueTypes?: Record<string, "number" | "boolean" | "string">;
}

const SetConfigurationValueModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  title,
  initialValue,
  configKey,
  nullableValueTypes = {},
}: Props) => {
  const label = configKey ? startCase(configKey).toLowerCase() : undefined;
  const overriddenType = configKey ? nullableValueTypes[configKey] : undefined;
  const [value, setValue] = useState<object | string | number | boolean | null>(null);
  const [valueItems, setValueItems] = useState<{ id: number; text: string }[]>([]);
  const [isInputValid, setIsInputValid] = useState(true);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialValue !== null && initialValue !== undefined) {
      setValue(initialValue);

      if (isArray(initialValue)) {
        setValueItems(
          initialValue.map((v, i) => {
            return { text: v, id: i };
          }),
        );
      } else {
        setValueItems([]);
      }
    } else {
      if (overriddenType === "number") {
        setValue(null);
      } else if (overriddenType === "boolean") {
        setValue(false as any);
      } else {
        setValue("" as any);
      }
      setValueItems([]);
    }
  }, [open, initialValue, configKey, overriddenType]);

  const handleTextChange = (e: any, data: TextAreaProps) => {
    setValue(String(data.value ?? "") as any);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setValue(text);

    if (e.target.validity.badInput) {
      setIsInputValid(false);
      return;
    }

    if (!text) {
      if (overriddenType === "number") {
        setValue(null);
        setIsInputValid(true);
      } else {
        setIsInputValid(false);
      }
      return;
    }

    const parsed = e.target.valueAsNumber;
    if (isNaN(parsed) || parsed < 0 || parsed > INT32_MAX_VALUE) {
      setIsInputValid(false);
      return;
    }

    setValue(parsed as any);
    setIsInputValid(true);
  };

  const handleMultipleChoiceChange = (e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    if (!isMultipleChoiceOption(value)) {
      return;
    }

    setValue({ ...value, value: String(data.value ?? "") });
  };

  const handleArrayItemChange = (id: number, text: string) => {
    setValueItems([...valueItems.map((i) => (i.id === id ? { id, text } : i))]);
  };

  const handleRemoveItem = (id: number) => {
    setValueItems([...remove(valueItems, (i) => i.id !== id)]);
  };

  const handleAddItem = () => {
    var newItem = { id: (maxBy(valueItems, (i) => i.id)?.id ?? 0) + 1, text: "" };
    setValueItems([...valueItems, newItem]);
  };

  const handleSubmit = () => {
    if (isMultipleChoiceOption(value)) {
      onSubmit(removeTypenameDeep(value));
      return;
    }

    if (isNumber(value)) {
      onSubmit(value as any);
      return;
    }

    if (value === null && overriddenType === "number") {
      onSubmit(null as any);
      return;
    }

    if (isArray(value)) {
      onSubmit(valueItems.map((i) => i.text));
      return;
    }
    onSubmit(value ?? (false as any));
    setValue(null);
  };

  const handleClose = () => {
    setValue(null);
    setValueItems([]);
    setIsInputValid(true);
    onClose();
  };

  return (
    <Modal open={open} size="large">
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <Form>
            {(isBoolean(value) || overriddenType === "boolean") && (
              <Form.Group>
                <label>{label}</label>
                <Form.Field>
                  <Checkbox
                    radio
                    checked={value === true}
                    label="Yes"
                    name="value"
                    onChange={() => {
                      setValue(true as any);
                    }}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    radio
                    checked={value === false}
                    label="No"
                    onChange={() => {
                      setValue(false as any);
                    }}
                  />
                </Form.Field>
              </Form.Group>
            )}
            {(isNumber(value) || overriddenType === "number") && (
              <Form.Group widths="equal">
                <Form.Input
                  label={label ?? "Value"}
                  name="value"
                  type="number"
                  min={0}
                  max={INT32_MAX_VALUE}
                  error={!isInputValid}
                  value={value === null ? "" : (value as any)}
                  onChange={handleNumberChange}
                />
              </Form.Group>
            )}
            {isString(value) && overriddenType === undefined && (
              <Form.Group widths="equal">
                <Form.TextArea label="Value" name="value" value={value} onChange={handleTextChange} />
              </Form.Group>
            )}
            {isMultipleChoiceOption(value) && (
              <Form.Group widths="equal">
                <Form.Select
                  label={label ?? "Value"}
                  name="value"
                  value={value.value}
                  options={value.options.map((option) => ({
                    key: option,
                    text: option,
                    value: option,
                  }))}
                  onChange={handleMultipleChoiceChange}
                />
              </Form.Group>
            )}
            {isArray(value) && (
              <>
                <ReactSortable list={valueItems} setList={setValueItems} handle=".handle">
                  {valueItems.map((item) => (
                    <Form.Input
                      key={item.id}
                      name="value"
                      value={item.text}
                      onChange={(e, p) => handleArrayItemChange(item.id, p.value)}
                      action={
                        <>
                          <Button className="handle" icon="arrows alternate vertical"></Button>
                          <Button icon="cancel" onClick={(e) => handleRemoveItem(item.id)}></Button>
                        </>
                      }
                    />
                  ))}
                </ReactSortable>
                <Button onClick={handleAddItem}>Add</Button>
              </>
            )}
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress || !isInputValid}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default SetConfigurationValueModal;
